import React from "react";
import PropTypes from "prop-types";
import { Button } from "@material-ui/core";

export interface AsyncButtonProps {
  component?: React.ElementType;
  onClick: (e: MouseEvent) => void;
  disabled?: boolean;
  disableClicked?: boolean;
  /**
   * True to keep the button disabled after the promise returned by onClick resolves.
   */
  keepDisabled?: boolean;
  throttle?: number;
  innerProps?: {};
  [componentProp: string]: any;
}

const AsyncButton = React.forwardRef(
  (
    {
      component: Component = Button,
      onClick,
      disabled,
      disableClicked = true,
      keepDisabled,
      throttle = 1000,
      innerProps = {},
      ...other
    }: AsyncButtonProps,
    ref
  ) => {
    const lastClicked = React.useRef(0);
    const [clicked, setClicked] = React.useState(false);
    const handleClick = React.useCallback(
      async (e) => {
        if (throttle != null && lastClicked.current + throttle > Date.now()) {
          return;
        }
        lastClicked.current = Date.now();
        try {
          setClicked(true);
          await onClick(e);
          if (!keepDisabled) {
            setClicked(false);
          }
        } catch (err) {
          setClicked(false);
          throw err;
        }
      },
      [onClick, throttle, keepDisabled]
    );

    return (
      <Component
        onClick={clicked ? undefined : handleClick}
        disabled={disabled || (disableClicked && clicked)}
        ref={ref}
        {...other}
      />
    );
  }
);

export default AsyncButton;
